import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Target, User, Ruler, Weight, Calendar, Pencil, KeyRound, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import StatusBadge from "@/components/StatusBadge";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }); } catch { return "—"; } };

export default function MemberProfile() {
  const [m, setM] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", dob: "", gender: "", height: "", weight: "", goal: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/members/me").then((r) => setM(r.data));
  useEffect(() => { load(); }, []);

  const openEdit = () => setForm({ name: m.name || "", phone: m.phone || "", dob: m.dob || "", gender: m.gender || "", height: m.height ?? "", weight: m.weight ?? "", goal: m.goal || "" }) || setEdit(true);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/me", {
        ...form,
        height: form.height === "" ? null : parseFloat(form.height),
        weight: form.weight === "" ? null : parseFloat(form.weight),
      });
      toast.success("Profilin güncellendi.");
      setEdit(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!m) return <Skeleton className="h-64 rounded-2xl bg-[#13131C]" />;

  return (
    <div data-testid="member-profile-page">
      <div className="flex flex-col items-center text-center py-4">
        <Avatar className="w-24 h-24 mb-3"><AvatarFallback className="bg-[#FF5500] text-white text-3xl font-bold">{m.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
        <h1 className="font-display font-extrabold text-2xl text-white">{m.name}</h1>
        <div className="mt-2"><StatusBadge status={m.status} /></div>
        <div className="flex gap-2 mt-4">
          <Button onClick={openEdit} data-testid="edit-profile-button" size="sm" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-semibold"><Pencil size={14} className="mr-1" /> Profili Düzenle</Button>
          <ChangePasswordDialog trigger={<Button size="sm" variant="secondary" className="bg-[#1C1C28] hover:bg-[#252536] text-white border border-[#2D2D3F]"><KeyRound size={14} className="mr-1" /> Şifre Değiştir</Button>} />
        </div>
      </div>

      <div className="space-y-2 mt-4">
        <Row icon={Mail} label="E-posta" value={m.email} />
        <Row icon={Phone} label="Telefon" value={m.phone || "—"} />
        <Row icon={User} label="Cinsiyet" value={m.gender || "—"} />
        <Row icon={Target} label="Hedef" value={m.goal || "—"} />
        <Row icon={Ruler} label="Boy" value={m.height ? `${m.height} cm` : "—"} />
        <Row icon={Weight} label="Kilo" value={m.weight ? `${m.weight} kg` : "—"} />
        <Row icon={User} label="Antrenör" value={m.trainer_name || "—"} />
        <Row icon={Calendar} label="Kayıt Tarihi" value={fmtDate(m.registration_date)} />
      </div>

      <p className="text-xs text-[#68687B] text-center mt-6">KVKK: Kişisel verilerin yalnızca yetkili personel tarafından görüntülenebilir.</p>

      <Dialog open={edit} onOpenChange={setEdit}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-sm" data-testid="edit-profile-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Profili Düzenle</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div><Label className="text-xs text-[#A0A0B2]">Ad Soyad</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" className="bg-[#0E0E14] border-[#2D2D3F] text-white mt-1" /></div>
            <div><Label className="text-xs text-[#A0A0B2]">Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" className="bg-[#0E0E14] border-[#2D2D3F] text-white mt-1" /></div>
            <div><Label className="text-xs text-[#A0A0B2]">Doğum Tarihi</Label>
              <Input type="date" value={form.dob || ""} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="edit-dob" className="bg-[#0E0E14] border-[#2D2D3F] text-white mt-1 [color-scheme:dark]" /></div>
            <div><Label className="text-xs text-[#A0A0B2]">Cinsiyet</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="edit-gender" className="bg-[#0E0E14] border-[#2D2D3F] text-white mt-1"><SelectValue placeholder="Seç" /></SelectTrigger>
                <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white">
                  <SelectItem value="Kadın">Kadın</SelectItem>
                  <SelectItem value="Erkek">Erkek</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#A0A0B2]">Boy (cm)</Label>
                <Input type="number" min="100" max="250" value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} data-testid="edit-height" placeholder="175" className="bg-[#0E0E14] border-[#2D2D3F] text-white mt-1" /></div>
              <div><Label className="text-xs text-[#A0A0B2]">Kilo (kg)</Label>
                <Input type="number" min="30" max="300" step="0.1" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} data-testid="edit-weight" placeholder="70" className="bg-[#0E0E14] border-[#2D2D3F] text-white mt-1" /></div>
            </div>
            <div><Label className="text-xs text-[#A0A0B2]">Hedef</Label>
              <Select value={form.goal} onValueChange={(v) => setForm({ ...form, goal: v })}>
                <SelectTrigger data-testid="edit-goal" className="bg-[#0E0E14] border-[#2D2D3F] text-white mt-1"><SelectValue placeholder="Hedef seç" /></SelectTrigger>
                <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white">
                  {["Kilo vermek", "Kas kazanmak", "Kondisyon", "Esneklik", "Genel sağlık"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(false)} className="text-[#A0A0B2]">Vazgeç</Button>
            <Button onClick={save} disabled={saving} data-testid="edit-save" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">{saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 p-3.5 rounded-xl bg-[#13131C] border border-[#222230]">
      <div className="w-9 h-9 rounded-lg bg-[#1B1B27] flex items-center justify-center"><Icon size={16} className="text-[#FF5500]" /></div>
      <span className="text-sm text-[#68687B] flex-1">{label}</span>
      <span className="text-sm font-semibold text-white text-right">{value}</span>
    </div>
  );
}
